import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import { getCarrierPerformanceStats } from '../../services/business/carrierPerformanceService';
import { getAllTransporters } from '../../services/firestore/transporterService';
import Button from '../../components/shared/Button';
import Combobox from '../../components/shared/Combobox';
import DataTable from '../../components/shared/DataTable';
import MetricPieChart from '../../components/reports/MetricPieChart';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { PERMISSIONS } from '../../constants/permissions';
import { buildUserIdentities, buildTransporterIdentities } from '../../services/business/transporterMatchingService';

const CarrierPerformanceBar = ({ percentage }) => {
    const val = Math.max(0, Math.min(100, Math.round(percentage)));

    return (
        <div style={{
            width: '100%',
            height: '24px',
            display: 'flex',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '2px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.4)',
            boxShadow: '0 0 10px rgba(0,0,0,0.3) inset'
        }}>
            {val > 0 && (
                <div style={{
                    width: `${val}%`,
                    height: '100%',
                    background: '#10b981',
                    boxShadow: '0 0 15px rgba(16,185,129,0.5)',
                    transition: 'width 1s ease-out'
                }} />
            )}
            {val < 100 && (
                <div style={{
                    width: `${100 - val}%`,
                    height: '100%',
                    background: '#ef4444',
                    boxShadow: '0 0 15px rgba(239,68,68,0.5)',
                    transition: 'width 1s ease-out'
                }} />
            )}
        </div>
    );
};

const SummaryCard = ({ title, value, subVal, icon, showBar = false, barValue }) => {
    const rawValue = barValue !== undefined ? barValue : parseFloat(value);

    return (
        <div className="card-premium glow-theme bg-glass" style={{ padding: '24px', minHeight: '150px', display: 'flex', flexDirection: 'column' }}>
            <div className="flex justify-between items-start mb-md">
                <span className="text-[11px] font-black text-secondary uppercase tracking-[0.3em] opacity-80">{title}</span>
                <span className="text-2xl">{icon}</span>
            </div>

            <div className="mt-auto">
                <div className="flex items-baseline gap-xs mb-md">
                    <span className="text-4xl font-black text-white">{value}</span>
                    {subVal && <span className="text-lg text-white/30 font-bold">{subVal}</span>}
                </div>

                {showBar && (
                    <CarrierPerformanceBar percentage={rawValue} />
                )}
            </div>
        </div>
    );
};

const MetricBar = ({ label, value, color, icon }) => (
    <div className="group">
        <div className="flex justify-between text-xs mb-2 items-center">
            <span className="text-secondary font-bold flex items-center gap-2">
                <span className="opacity-0 group-hover:opacity-100 transition-smooth">{icon}</span>
                {label}
            </span>
            <span className="text-white font-black bg-white/5 py-1 px-2 rounded-lg border border-white/5">{value?.toFixed(1)}%</span>
        </div>
        <div className="h-[6px] bg-white/5 rounded-full overflow-hidden border border-white/5">
            <div
                className="h-full transition-smooth group-hover:brightness-125 shadow-[0_0_15px_rgba(0,0,0,0.5)]"
                style={{ width: `${value}%`, background: color }}
            />
        </div>
    </div>
);

const DATE_OPTIONS = [
    { id: 'custom', label: 'Özel Aralık' },
    { id: 'last_month', label: 'Geçen Ay' },
    { id: 'last_3_months', label: 'Son 3 Ay' },
    { id: 'last_6_months', label: 'Son 6 Ay' },
    { id: 'last_year', label: 'Son 1 Yıl' },
    { id: 'all_time', label: 'Tüm Zamanlar' }
];

const CarrierPerformanceReport = ({ userData }) => {
    const { user, can } = usePermissions(userData);
    const { success, error: toastError } = useToast();
    const navigate = useNavigate();

    // State
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [transporters, setTransporters] = useState([]);

    // Filters
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [selectedDateOption, setSelectedDateOption] = useState(DATE_OPTIONS[0]);
    const [selectedTransporter, setSelectedTransporter] = useState({ id: 'all', label: 'Tüm Nakliyeciler' });
    const [pivotMetric, setPivotMetric] = useState('Skor'); // pivot state for comparison chart
    const [activeTooltip, setActiveTooltip] = useState(null);

    // Initial Load
    useEffect(() => {
        if (user?.tenantId) {
            loadTransporters();
            loadReport();
        }
    }, [user?.tenantId]);

    const loadTransporters = async () => {
        try {
            const data = await getAllTransporters(user.tenantId);
            let list = data;

            if (userData?.role === 'transporter') {
                const myIdentities = buildUserIdentities(userData);
                list = data.filter(t => {
                    const tIdentities = buildTransporterIdentities(t);
                    return Array.from(tIdentities).some(id => myIdentities.has(id));
                });
            }

            const formatted = [
                ...(userData?.role === 'transporter' ? [] : [{ id: 'all', label: 'Tüm Nakliyeciler' }]),
                ...list.map(t => ({ id: t.id, label: t.companyName }))
            ];
            setTransporters(formatted);

            // Auto-select if transporter
            if (userData?.role === 'transporter' && list.length > 0) {
                setSelectedTransporter({ id: list[0].id, label: list[0].companyName });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDateOptionChange = (option) => {
        setSelectedDateOption(option);
        const now = new Date();
        let start = new Date();
        let end = new Date();

        switch (option.id) {
            case 'last_month':
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                end = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case 'last_3_months':
                start.setDate(now.getDate() - 90);
                break;
            case 'last_6_months':
                start.setDate(now.getDate() - 180);
                break;
            case 'last_year':
                start.setDate(now.getDate() - 365);
                break;
            case 'all_time':
                start = new Date(2020, 0, 1);
                break;
            default:
                return; // Don't change for custom
        }

        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    };

    // Trigger loadReport when filters change
    useEffect(() => {
        if (user?.tenantId && selectedTransporter) {
            loadReport();
        }
    }, [user?.tenantId, dateRange.start, dateRange.end, selectedTransporter?.id]);

    const loadReport = async () => {
        setLoading(true);
        try {
            let tId = selectedTransporter?.id === 'all' ? null : selectedTransporter?.id;

            // Forced security for transporters
            if (userData?.role === 'transporter') {
                if (!tId || tId === 'all') {
                    // Try to find correct ID from master list if possible
                    const data = await getAllTransporters(user.tenantId);
                    const myIdentities = buildUserIdentities(userData);
                    const match = data.find(t => {
                        const tIdentities = buildTransporterIdentities(t);
                        return Array.from(tIdentities).some(id => myIdentities.has(id));
                    });
                    tId = match?.id || userData.transporterId;
                }
            }

            const data = await getCarrierPerformanceStats(user.tenantId, {
                startDate: dateRange.start,
                endDate: dateRange.end,
                transporterId: tId
            });
            setStats(data);
        } catch (err) {
            toastError("Rapor oluşturulurken hata oluştu: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper for table headers with info tooltips
    const InfoHeader = (key, label, tooltip) => (
        <div
            className="flex items-center gap-2 relative inline-flex"
            onMouseEnter={() => setActiveTooltip(key)}
            onMouseLeave={() => setActiveTooltip(null)}
        >
            <span className="whitespace-nowrap">{label}</span>
            {activeTooltip === key && (
                <div style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 12px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '240px',
                    backgroundColor: '#1e293b',
                    color: '#ffffff',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    zIndex: 9999,
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.4)',
                    pointerEvents: 'none'
                }}>
                    {tooltip}
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        marginLeft: '-6px',
                        borderWidth: '6px',
                        borderStyle: 'solid',
                        borderColor: '#1e293b transparent transparent transparent'
                    }} />
                </div>
            )}
        </div>
    );

    // Helper for drill-down links
    const LinkCell = (row, value, type = 'all', colorClass = 'text-blue-400') => {
        if (!value || value === 0 || value === '0') return <span style={{ opacity: 0.4 }}>0</span>;

        const handleClick = (e) => {
            e.stopPropagation();
            const params = new URLSearchParams();
            params.set('transporter', row.id || row.name);
            params.set('startDate', dateRange.start);
            params.set('endDate', dateRange.end);

            let status = '';
            switch (type) {
                case 'onTime': status = 'completed'; break;
                case 'cancelled': status = 'cancelled'; break;
                case 'delayed': status = 'delayed'; break;
            }
            if (status) params.set('status', status);
            navigate(`/operations/tracking?${params.toString()}`);
        };

        return (
            <button
                onClick={handleClick}
                className={`${colorClass} hover:opacity-75 transition-all cursor-pointer bg-transparent border-none p-0 m-0 outline-none`}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    fontWeight: 'inherit',
                    color: 'inherit',
                    fontSize: 'inherit'
                }}
            >
                {value}
            </button>
        );
    };

    // Columns for Raw Counts Table
    const countColumns = [
        {
            header: 'Nakliyeci',
            accessor: 'name',
            cellStyle: () => ({ color: 'var(--text-primary)', fontWeight: '700' })
        },
        {
            header: InfoHeader('total', 'Toplam', 'Seçilen dönemdeki toplam rezervasyon sayısı'),
            accessor: (row) => LinkCell(row, row.totalReservations, 'all')
        },
        {
            header: InfoHeader('onTime', 'Zamanında', 'Planlanan saatte veya tolerans payı içinde tesise giren araçlar'),
            accessor: (row) => LinkCell(row, row.raw?.onTime || 0, 'onTime'),
            cellStyle: () => ({ color: '#10b981', fontWeight: 'bold' })
        },
        {
            header: InfoHeader('delayed', 'Geciken', 'Randevu saati geçmesine rağmen gelmeyen veya geç giren araçlar'),
            accessor: (row) => LinkCell(row, row.raw?.delayed || 0, 'delayed'),
            cellStyle: (row) => ({
                color: (row.raw?.delayed || 0) > 0 ? '#ef4444' : 'inherit',
                opacity: (row.raw?.delayed || 0) > 0 ? 1 : 0.4
            })
        },
        {
            header: InfoHeader('cancel', 'İptal', 'İptal edilen veya reddedilen rezervasyon sayısı'),
            accessor: (row) => LinkCell(row, row.raw?.cancelled || 0, 'cancelled')
        },
        {
            header: InfoHeader('ref', 'Revize', 'Sürücü tarafından randevu saati değiştirilen seferler'),
            accessor: (row) => LinkCell(row, row.raw?.revised || 0, 'revised')
        },
        {
            header: InfoHeader('plate', 'Plaka Rev.', 'Tesis girişinde plaka bilgisi düzeltilen seferler'),
            accessor: (row) => LinkCell(row, row.raw?.plateChanged || 0, 'plate')
        },
        {
            header: InfoHeader('driver', 'Sürücü Rev.', 'Tesis girişinde sürücü ismi düzeltilen seferler'),
            accessor: (row) => LinkCell(row, row.raw?.driverChanged || 0, 'driver')
        }
    ];

    // Columns for Percentage Analysis Table
    const percentColumns = [
        {
            header: 'Nakliyeci',
            accessor: 'name',
            cellStyle: () => ({ color: 'var(--text-primary)', fontWeight: '700' })
        },
        {
            header: InfoHeader('onTimePerc', 'Zamanında %', 'Rezervasyon saatine ne kadar sadık kalındığı (% olarak)'),
            accessor: (row) => `${(row.metrics?.onTimeRate || 0).toFixed(1)}%`,
            cellStyle: (row) => ({
                color: (row.metrics?.onTimeRate || 0) >= 90 ? '#10b981' : ((row.metrics?.onTimeRate || 0) >= 70 ? '#f59e0b' : '#ef4444'),
                fontWeight: 'bold'
            })
        },
        {
            header: InfoHeader('delayedPerc', 'Gecikme %', 'Tesis girişini randevu saatinden sonra yapan veya yapmayan araç oranı'),
            accessor: (row) => `${((row.raw?.delayed || 0) / (row.totalReservations || 1) * 100).toFixed(1)}%`,
            cellStyle: (row) => ({
                color: (row.raw?.delayed || 0) > 0 ? '#ef4444' : 'inherit'
            })
        },
        {
            header: InfoHeader('revisedPerc', 'Revizesiz Sefer %', 'Rezervasyonun oluşturulduğu andan itibaren hiç değiştirilmeden tamamlanma oranı'),
            accessor: (row) => `${(row.metrics?.revisionRate || 0).toFixed(1)}%`
        },
        {
            header: InfoHeader('platePerc', 'Plaka Doğruluk %', 'Randevudaki plaka bilgisinin tesis girişindekiyle eşleşme oranı'),
            accessor: (row) => `${(row.metrics?.plateAccuracy || 0).toFixed(1)}%`
        },
        {
            header: InfoHeader('driverPerc', 'Sürücü Doğruluk %', 'Randevudaki sürücü isminin tesis girişindekiyle eşleşme oranı'),
            accessor: (row) => `${(row.metrics?.driverAccuracy || 0).toFixed(1)}%`
        },
        {
            header: InfoHeader('skor', 'SKOR', 'Tüm performans metriklerinin ağırlıklı ortalaması (10 üzerinden)'),
            accessor: (row) => row.metrics?.score || 0,
            cellStyle: (row) => ({
                color: 'white',
                background: (row.metrics?.score || 0) >= 8 ? 'rgba(16, 185, 129, 0.6)' : ((row.metrics?.score || 0) >= 5 ? 'rgba(245, 158, 11, 0.6)' : 'rgba(239, 68, 68, 0.6)'),
                padding: '4px 12px',
                borderRadius: '20px',
                fontWeight: '900',
                textAlign: 'center',
                boxShadow: 'inset 0 0 10px rgba(255,255,255,0.2)',
                backdropFilter: 'blur(4px)'
            })
        }
    ];

    // Chart Data Preparation
    const pieDataOnTime = useMemo(() => {
        if (!stats || !stats.carriers) return [];
        const isAll = !selectedTransporter || selectedTransporter.id === 'all';
        const carrier = !isAll ? stats.carriers[0] : null;

        const onTime = carrier ? (carrier.raw?.onTime || 0) : stats.carriers.reduce((sum, c) => sum + (c.raw?.onTime || 0), 0);
        const totalEntered = carrier ? (carrier.raw?.totalEntered || 0) : stats.carriers.reduce((sum, c) => sum + (c.raw?.totalEntered || 0), 0);

        if (totalEntered === 0) {
            return [
                { name: 'Veri Yok', value: 1, color: 'rgba(255, 255, 255, 0.05)' }
            ];
        }

        return [
            { name: 'Zamanında', value: onTime, color: 'rgba(16, 185, 129, 0.8)' },
            { name: 'Gecikme', value: Math.max(0, totalEntered - onTime), color: 'rgba(239, 68, 68, 0.8)' }
        ];
    }, [stats, selectedTransporter]);

    const pieDataAccuracy = useMemo(() => {
        if (!stats || !stats.carriers) return [];
        const isAll = !selectedTransporter || selectedTransporter.id === 'all';
        const carrier = !isAll ? stats.carriers[0] : null;

        const plateAcc = carrier ? (carrier.metrics?.plateAccuracy || 0) : (stats.summary?.avgAccuracyRate || 0);
        const driverAcc = carrier ? (carrier.metrics?.driverAccuracy || 0) : (stats.summary?.avgAccuracyRate || 0);
        const avgAcc = (plateAcc + driverAcc) / 2;

        if (avgAcc === 0 && (100 - avgAcc) === 100 && stats.carriers.length === 0) {
            return [{ name: 'Veri Yok', value: 1, color: 'rgba(255, 255, 255, 0.05)' }];
        }

        return [
            { name: 'Doğru Veri', value: avgAcc || 0, color: 'rgba(59, 130, 246, 0.8)' },
            { name: 'Hatalı/Eksik', value: Math.max(0, 100 - (avgAcc || 0)), color: 'rgba(245, 158, 11, 0.8)' }
        ];
    }, [stats, selectedTransporter]);

    const barDataComparison = useMemo(() => {
        if (!stats || !stats.carriers || (selectedTransporter && selectedTransporter.id !== 'all')) return [];
        return stats.carriers
            .map(c => ({
                name: c.name,
                Skor: c.metrics?.score || 0,
                'Zamanında %': c.metrics?.onTimeRate || 0,
                'Doğruluk %': ((c.metrics?.plateAccuracy || 0) + (c.metrics?.driverAccuracy || 0)) / 2
            }))
            .sort((a, b) => b[pivotMetric] - a[pivotMetric])
            .slice(0, 10);
    }, [stats, selectedTransporter, pivotMetric]);


    return (
        <div className="container py-lg space-y-xl animated-entry">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-xl card-premium glow-theme gap-lg mb-xl">
                <div className="flex items-center gap-md">
                    <div className="p-sm bg-primary/10 rounded-2xl border border-primary/20 shadow-lg">
                        <span style={{ fontSize: '2rem' }}>📊</span>
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-primary uppercase tracking-tighter m-0">
                            Nakliyeci Karnesi
                        </h1>
                        <p className="text-secondary text-xs font-bold uppercase tracking-widest opacity-70">
                            Performans & Pivot Analiz Raporu
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-md items-center w-full md:w-auto">
                    <div className="w-[180px]">
                        <Combobox
                            items={DATE_OPTIONS}
                            value={selectedDateOption?.id}
                            onChange={handleDateOptionChange}
                            placeholder="Tarih Seçimi"
                            className="glass-input"
                        />
                    </div>

                    <div className="flex items-center bg-glass p-1 rounded-xl border border-white/5 h-[42px]">
                        <input
                            type="date"
                            className="glass-input !border-0 !bg-transparent text-sm p-1"
                            value={dateRange.start}
                            onChange={(e) => {
                                setDateRange(prev => ({ ...prev, start: e.target.value }));
                                setSelectedDateOption(DATE_OPTIONS.find(o => o.id === 'custom'));
                            }}
                        />
                        <span className="text-secondary mx-1">/</span>
                        <input
                            type="date"
                            className="glass-input !border-0 !bg-transparent text-sm p-1"
                            value={dateRange.end}
                            onChange={(e) => {
                                setDateRange(prev => ({ ...prev, end: e.target.value }));
                                setSelectedDateOption(DATE_OPTIONS.find(o => o.id === 'custom'));
                            }}
                        />
                    </div>

                    <div className="w-[180px]">
                        <Combobox
                            items={transporters}
                            value={selectedTransporter?.id}
                            onChange={(item) => setSelectedTransporter(item)}
                            placeholder="Tüm Nakliyeciler"
                            className="glass-input"
                        />
                    </div>

                    <Button
                        variant="primary"
                        icon="🔍"
                        onClick={loadReport}
                        disabled={loading}
                        className="shadow-glow-blue"
                    >
                        Karşılaştır
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center p-20 card-premium border-0 shadow-none bg-transparent">
                    <div className="loading-spinner mb-md" />
                    <div className="text-primary font-black animate-pulse uppercase tracking-widest text-sm">Veriler İşleniyor...</div>
                </div>
            ) : stats ? (
                <>
                    {/* Summary Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg mb-xl">
                        <SummaryCard
                            title="GENEL SKOR"
                            value={selectedTransporter?.id !== 'all' ? (stats.carriers[0]?.metrics.score || 0) : stats.summary.averageScore}
                            subVal="/ 10"
                            icon="🏆"
                            showBar={true}
                            barValue={(selectedTransporter?.id !== 'all' ? (stats.carriers[0]?.metrics.score || 0) : stats.summary.averageScore) * 10}
                        />
                        <SummaryCard
                            title="ZAMANINDA VARIŞ"
                            value={selectedTransporter?.id !== 'all' ? (stats.carriers[0]?.metrics.onTimeRate || 0).toFixed(1) : stats.summary.avgOnTimeRate}
                            subVal="%"
                            icon="⏱️"
                            showBar={true}
                            barValue={selectedTransporter?.id !== 'all' ? (stats.carriers[0]?.metrics.onTimeRate || 0) : stats.summary.avgOnTimeRate}
                        />
                        <SummaryCard
                            title="VERİ DOĞRULUĞU"
                            value={selectedTransporter?.id !== 'all' ? (((stats.carriers[0]?.metrics.plateAccuracy || 0) + (stats.carriers[0]?.metrics.driverAccuracy || 0)) / 2).toFixed(1) : stats.summary.avgAccuracyRate}
                            subVal="%"
                            icon="✅"
                            showBar={true}
                            barValue={selectedTransporter?.id !== 'all' ? (((stats.carriers[0]?.metrics.plateAccuracy || 0) + (stats.carriers[0]?.metrics.driverAccuracy || 0)) / 2) : stats.summary.avgAccuracyRate}
                        />
                    </div>

                    {/* Percentage Analysis Table */}
                    <div className="card-premium glow-theme p-xl bg-glass transition-smooth hover:bg-glass/20 mb-xl">
                        <div className="flex justify-between items-center mb-xl">
                            <h3 className="text-lg font-black uppercase tracking-tighter m-0 flex items-center gap-sm">
                                <span className="p-xs bg-blue-500/20 rounded-lg text-blue-400">📈</span>
                                PERFORMANS ANALİZİ (YÜZDESEL)
                            </h3>
                            <Button variant="secondary" size="sm" onClick={() => {
                                const csvHeaders = "Nakliyeci,Zamanında %,Revizesiz Sefer %,Plaka Doğruluk %,Sürücü Doğruluk %,Skor\n";
                                const csvBody = stats.carriers.map(c =>
                                    `${c.name},${(c.metrics?.onTimeRate || 0).toFixed(1)}%,${(c.metrics?.revisionRate || 0).toFixed(1)}%,${(c.metrics?.plateAccuracy || 0).toFixed(1)}%,${(c.metrics?.driverAccuracy || 0).toFixed(1)}%,${c.metrics?.score || 0}`
                                ).join('\n');
                                const blob = new Blob([csvHeaders + csvBody], { type: 'text/csv' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'carrier-performance-percentages.csv';
                                a.click();
                            }}>Dışa Aktar (CSV)</Button>
                        </div>
                        <DataTable
                            columns={percentColumns}
                            data={stats.carriers}
                            pagination
                        />
                    </div>

                    {/* Raw Data Repository */}
                    <div className="card-premium glow-theme p-xl bg-glass transition-smooth hover:bg-glass/20">
                        <div className="flex justify-between items-center mb-xl">
                            <h3 className="text-lg font-black uppercase tracking-tighter m-0 flex items-center gap-sm">
                                <span className="p-xs bg-emerald-500/20 rounded-lg text-emerald-400">📄</span>
                                OPERASYONEL VERİLER (ADET)
                            </h3>
                            <Button variant="secondary" size="sm" onClick={() => {
                                const csvHeaders = "Nakliyeci,Toplam,Zamanında,Geciken,İptal,Revize,Plaka Revize,Sürücü Revize\n";
                                const csvBody = stats.carriers.map(c =>
                                    `${c.name},${c.totalReservations},${c.raw?.onTime || 0},${c.raw?.delayed || 0},${c.raw?.cancelled || 0},${c.raw?.revised || 0},${c.raw?.plateChanged || 0},${c.raw?.driverChanged || 0}`
                                ).join('\n');
                                const blob = new Blob([csvHeaders + csvBody], { type: 'text/csv' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'carrier-performance-counts.csv';
                                a.click();
                            }}>Dışa Aktar (CSV)</Button>
                        </div>
                        <DataTable
                            columns={countColumns}
                            data={stats.carriers}
                            pagination
                        />
                    </div>
                </>
            ) : (
                <div className="card-premium glow-theme p-20 text-center opacity-50">
                    <span style={{ fontSize: '3rem' }}>🔍</span>
                    <h3 className="mt-md">Veri Bulunamadı</h3>
                    <p>Seçilen tarih aralığında ve filtrede herhangi bir rezervasyon kaydı bulunmamaktadır.</p>
                </div>
            )}
        </div>
    );
};

export default CarrierPerformanceReport;
